import { useState, useMemo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Card,
  CardContent,
  Button,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Storefront,
  Event as EventIcon,
  Schedule,
  AttachMoney,
} from '@mui/icons-material';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { TranslucentContainer, LinkButton } from '../../../../../../components';
import { formatTime } from '../../../../../../utils';
import { APP_ROUTES } from '../../../../../../constants/app-routes';
import { useStorageImage } from '../../../../../../hooks/useStorageImage';
import { useIsMobile } from '../../../../../../hooks/useIsMobile';
import DefaultPhoto from '../../../../../../assets/img/default-img.png';

function CalendarView({
  events,
  retailProfiles: retailProfilesProp,
  dateFilterStart,
  dateFilterEnd,
  priceFilterMin,
  priceFilterMax,
}) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const theme = useTheme();
  // Check if screen is smaller than lg breakpoint (where panel appears below calendar)
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

  // Get retail profiles from Redux (fallback if not provided as prop)
  const { profiles: retailProfilesFromRedux } = useSelector((store) => store.retailProfiles);

  // Use prop if provided, otherwise fall back to Redux
  const retailProfiles = retailProfilesProp || retailProfilesFromRedux;

  const [currentMonth, setCurrentMonth] = useState(moment());
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [selectedDayItems, setSelectedDayItems] = useState([]);
  const [modalDate, setModalDate] = useState(null); // Store the date for modal items
  const [selectedItemType, setSelectedItemType] = useState(null); // 'event' or 'retail'
  const [selectedDate, setSelectedDate] = useState(null); // For retail opportunities, store the clicked date

  // Simplified color scheme
  const EVENT_COLOR = '#EE7B57'; // Orange for events
  const RETAIL_COLOR = '#3c99ba'; // Turquoise for retail

  // Note: Retail profiles are now fetched by the parent VendorDashboard component
  // This prevents a render loop where fetching sets loading state, which hides this component,
  // which unmounts it, and when it remounts it fetches again

  // Load selected event from localStorage and find it in the events array
  // Only restore if selection is less than 1 hour old
  const [selectedEvent, setSelectedEvent] = useState(() => {
    const savedEventId = localStorage.getItem('vendorDashboardSelectedEventId');
    const savedTimestamp = localStorage.getItem('vendorDashboardSelectedEventTimestamp');

    if (savedEventId && savedTimestamp && events.length > 0) {
      const savedTime = parseInt(savedTimestamp, 10);
      const currentTime = Date.now();
      const oneHourInMs = 60 * 60 * 1000; // 1 hour in milliseconds

      // Check if the saved selection is less than 1 hour old
      if (currentTime - savedTime < oneHourInMs) {
        const event = events.find((e) => e.id === parseInt(savedEventId, 10));
        return event || null;
      }
      // If expired, clear the stored data
      localStorage.removeItem('vendorDashboardSelectedEventId');
      localStorage.removeItem('vendorDashboardSelectedEventTimestamp');
    }
    return null;
  });

  // Get the image URL for the selected event using the same hook as grid view
  const selectedEventPhoto = useStorageImage(selectedEvent?.photo);

  // Ref for event details panel (used on small screens where it appears below)
  const eventDetailsPanelRef = useRef(null);

  // Helper function to format retail times from 24-hour to 12-hour AM/PM
  const formatRetailTime = (timeString) => {
    if (!timeString) return '';
    // timeString is in format like "14:00:00" or "14:00"
    return moment(timeString, ['HH:mm:ss', 'HH:mm']).format('h:mm A');
  };

  // Color palette for venues - 6 distinct, vibrant colors
  const venueColors = [
    '#EE7B57', // Orange (brand color)
    '#3c99ba', // Turquoise
    '#FF6B9D', // Pink
    '#95E1D3', // Mint
    '#F38181', // Coral
    '#AA96DA', // Purple
  ];

  // Create a consistent color mapping for venues
  const venueColorMap = useMemo(() => {
    const uniqueVenues = [...new Set(events.map((e) => e.venue?.id).filter(Boolean))];
    const colorMap = {};
    uniqueVenues.forEach((venueId, index) => {
      colorMap[venueId] = venueColors[index % venueColors.length];
    });
    return colorMap;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  // Get color for a specific event's venue
  const getEventColor = (event) => {
    const venueId = event.venue?.id;
    return venueId ? venueColorMap[venueId] : venueColors[0];
  };

  // Navigate to previous month
  const previousMonth = () => {
    setCurrentMonth(currentMonth.clone().subtract(1, 'month'));
  };

  // Navigate to next month
  const nextMonth = () => {
    setCurrentMonth(currentMonth.clone().add(1, 'month'));
  };

  // Get all days in the current month view (including padding days from prev/next month)
  const renderCalendarDays = () => {
    const startOfMonth = currentMonth.clone().startOf('month');
    const endOfMonth = currentMonth.clone().endOf('month');
    const startDate = startOfMonth.clone().startOf('week');
    const endDate = endOfMonth.clone().endOf('week');

    const calendar = [];
    const day = startDate.clone();

    while (day.isSameOrBefore(endDate)) {
      calendar.push(day.clone());
      day.add(1, 'day');
    }

    return calendar;
  };

  // Events are now filtered in the parent VendorDashboard component
  // We just use the events prop directly

  // Get events for a specific date (only future/today, and within date filter if set)
  const getEventsForDate = (date) => {
    const today = moment().startOf('day');
    const dateToCheck = date.clone().startOf('day');

    // Only show events for today or future dates
    if (dateToCheck.isBefore(today)) {
      return [];
    }

    // If date filters are applied, check if this date is within the range
    if (dateFilterStart || dateFilterEnd) {
      const dateOnly = dateToCheck.clone();

      // If start date is set, date must be on or after it
      if (dateFilterStart) {
        const startDate = moment(dateFilterStart).startOf('day');
        if (dateOnly.isBefore(startDate)) {
          return [];
        }
      }

      // If end date is set, date must be on or before it
      if (dateFilterEnd) {
        const endDate = moment(dateFilterEnd).startOf('day');
        if (dateOnly.isAfter(endDate)) {
          return [];
        }
      }
    }

    return events.filter((event) => {
      const eventDate = moment(event.parameters_event?.event_date).format('YYYY-MM-DD');
      return date.format('YYYY-MM-DD') === eventDate;
    });
  };

  // Map retail schedules to calendar dates (recurring by day of week, only future/today)
  const getRetailOpportunitiesForDate = (date) => {
    const opportunities = [];
    const today = moment().startOf('day');
    const dateToCheck = date.clone().startOf('day');

    // Only show retail opportunities for today or future dates
    if (dateToCheck.isBefore(today)) {
      return [];
    }

    // If date filters are applied, check if this specific date is within the range
    if (dateFilterStart || dateFilterEnd) {
      const dateOnly = dateToCheck.clone();

      // If start date is set, this date must be on or after it
      if (dateFilterStart) {
        const startDate = moment(dateFilterStart).startOf('day');
        if (dateOnly.isBefore(startDate)) {
          return [];
        }
      }

      // If end date is set, this date must be on or before it
      if (dateFilterEnd) {
        const endDate = moment(dateFilterEnd).startOf('day');
        if (dateOnly.isAfter(endDate)) {
          return [];
        }
      }
    }

    const dayOfWeek = date.format('dddd').toLowerCase();

    (retailProfiles || []).forEach((profile) => {
      if (profile.status !== 'active' || !profile.schedules) return;

      // Check if profile is within date range
      const profileStart = profile.start_date ? moment(profile.start_date) : null;
      const profileEnd = profile.end_date ? moment(profile.end_date) : null;

      if (profileStart && date.isBefore(profileStart, 'day')) return;
      if (profileEnd && date.isAfter(profileEnd, 'day')) return;

      // Find schedules that match this day of week
      const matchingSchedules = (profile.schedules || []).filter(
        (schedule) => schedule.day_of_week?.toLowerCase() === dayOfWeek,
      );

      matchingSchedules.forEach((schedule) => {
        // Filter by price range if applied - check if this schedule's price is within range
        if (priceFilterMin || priceFilterMax) {
          const schedulePrice = parseFloat(schedule.price_per_hour) || 0;
          const minPrice = priceFilterMin ? parseFloat(priceFilterMin) : 0;
          const maxPrice = priceFilterMax ? parseFloat(priceFilterMax) : Infinity;

          // Only include this schedule if price is within range
          if (schedulePrice >= minPrice && schedulePrice <= maxPrice) {
            opportunities.push({
              id: `retail-${profile.id}-${schedule.id}`,
              profile,
              schedule,
              type: 'retail',
            });
          }
        } else {
          // No price filter, include all schedules
          opportunities.push({
            id: `retail-${profile.id}-${schedule.id}`,
            profile,
            schedule,
            type: 'retail',
          });
        }
      });
    });

    return opportunities;
  };

  // Get all items (events + retail) for a date
  const getItemsForDate = (date) => {
    const dateEvents = getEventsForDate(date);
    const retailOpps = getRetailOpportunitiesForDate(date);

    return {
      events: dateEvents,
      retail: retailOpps,
      all: [
        ...dateEvents.map((e) => ({ ...e, itemType: 'event' })),
        ...retailOpps.map((r) => ({ ...r, itemType: 'retail' })),
      ],
    };
  };

  // Handle event selection and save to localStorage with timestamp
  const handleEventClick = (event) => {
    setSelectedEvent(event);
    localStorage.setItem('vendorDashboardSelectedEventId', event.id.toString());
    localStorage.setItem('vendorDashboardSelectedEventTimestamp', Date.now().toString());

    // On small screens (where panel appears below), scroll to event details after a short delay
    if (isSmallScreen && eventDetailsPanelRef.current) {
      setTimeout(() => {
        eventDetailsPanelRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    }
  };

  // Handle closing the event panel
  const handleCloseEventPanel = () => {
    setSelectedEvent(null);
    setSelectedItemType(null);
    setSelectedDate(null);
    localStorage.removeItem('vendorDashboardSelectedEventId');
    localStorage.removeItem('vendorDashboardSelectedEventTimestamp');
  };

  // Handle opening day modal to show all items
  const handleOpenDayModal = (dayItems, date) => {
    setSelectedDayItems(dayItems);
    setModalDate(date);
    setDayModalOpen(true);
  };

  // Handle closing day modal
  const handleCloseDayModal = () => {
    setDayModalOpen(false);
    setSelectedDayItems([]);
    setModalDate(null);
  };

  // Check if we need to restore a selected event when events change
  useEffect(() => {
    if (!selectedEvent && events.length > 0) {
      const savedEventId = localStorage.getItem('vendorDashboardSelectedEventId');
      const savedTimestamp = localStorage.getItem('vendorDashboardSelectedEventTimestamp');

      if (savedEventId && savedTimestamp) {
        const savedTime = parseInt(savedTimestamp, 10);
        const currentTime = Date.now();
        const oneHourInMs = 60 * 60 * 1000; // 1 hour in milliseconds

        // Check if the saved selection is less than 1 hour old
        if (currentTime - savedTime < oneHourInMs) {
          const event = events.find((e) => e.id === parseInt(savedEventId, 10));
          if (event) {
            setSelectedEvent(event);
          } else {
            // Event not found, clear the saved data
            localStorage.removeItem('vendorDashboardSelectedEventId');
            localStorage.removeItem('vendorDashboardSelectedEventTimestamp');
          }
        } else {
          // Expired, clear the saved data
          localStorage.removeItem('vendorDashboardSelectedEventId');
          localStorage.removeItem('vendorDashboardSelectedEventTimestamp');
        }
      }
    }
  }, [events, selectedEvent]);

  const eventDetails = (eventId) => {
    if (eventId === undefined) return '';
    return APP_ROUTES?.EVENT_DETAILS.replace(':eventId', eventId);
  };

  const eventRegisterLink = (eventId) => {
    if (!eventId) return '';
    return APP_ROUTES?.VENDOR_EVENT_REGISTRATION.replace(':id', eventId);
  };

  const retailDetails = (profileId) => {
    if (!profileId) return '';
    return APP_ROUTES?.RETAIL_OPPORTUNITY_DETAILS.replace(':profileId', profileId);
  };

  const retailBooking = (profileId) => {
    if (!profileId) return '';
    return APP_ROUTES?.RETAIL_BOOKING.replace(':id', profileId);
  };

  // Generic handler for clicking any item (event or retail)
  const handleItemClick = (item, type, clickedDate = null) => {
    if (type === 'event') {
      setSelectedEvent(item);
      setSelectedItemType('event');
      setSelectedDate(null);
      localStorage.setItem('vendorDashboardSelectedEventId', item.id.toString());
      localStorage.setItem('vendorDashboardSelectedEventTimestamp', Date.now().toString());
    } else {
      setSelectedEvent(item.profile);
      setSelectedItemType('retail');
      // Store the specific date that was clicked for retail opportunities
      setSelectedDate(clickedDate);
    }

    if (isSmallScreen && eventDetailsPanelRef.current) {
      setTimeout(() => {
        eventDetailsPanelRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    }
  };

  const calendarDays = renderCalendarDays();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Get events for the current month, sorted by date
  const eventsThisMonth = useMemo(
    () =>
      events
        .filter((event) => {
          const eventDate = moment(event.parameters_event?.event_date);
          return eventDate.isSame(currentMonth, 'month');
        })
        .sort((a, b) => {
          const dateA = moment(a.parameters_event?.event_date);
          const dateB = moment(b.parameters_event?.event_date);
          return dateA.diff(dateB);
        }),
    [events, currentMonth],
  );

  // Mobile Agenda View
  if (isMobile) {
    return (
      <Grid container marginTop={5} justifyContent="center" spacing={3}>
        <Grid item xs={12}>
          <TranslucentContainer>
            <Box sx={{ padding: 3 }}>
              {/* Month Navigation */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                marginBottom={3}>
                <IconButton onClick={previousMonth} sx={{ color: '#EE7B57' }}>
                  <ChevronLeft />
                </IconButton>
                <Typography variant="h5" fontWeight="bold" color="#EE7B57">
                  {currentMonth.format('MMMM YYYY')}
                </Typography>
                <IconButton onClick={nextMonth} sx={{ color: '#EE7B57' }}>
                  <ChevronRight />
                </IconButton>
              </Box>

              {/* Agenda List */}
              {eventsThisMonth.length > 0 ? (
                <Box>
                  {eventsThisMonth.map((event) => {
                    const eventColor = getEventColor(event);
                    return (
                      <Card
                        key={event.id}
                        sx={{
                          marginBottom: 2,
                          borderLeft: `4px solid ${eventColor}`,
                          '&:hover': { boxShadow: 3 },
                        }}
                        onClick={() => handleEventClick(event)}>
                        <CardContent>
                          <Typography variant="h6" color={eventColor} gutterBottom>
                            {event.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            📅 {moment(event.parameters_event?.event_date).format('dddd, MMMM Do')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            🕒 {formatTime(event?.parameters_event?.hour_start_event, 'h:mm A')} -{' '}
                            {formatTime(event?.parameters_event?.hour_end_event, 'h:mm A')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            📍 {event.venue?.name || 'Venue TBD'}
                          </Typography>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              ) : (
                <Box textAlign="center" padding={4}>
                  <Typography variant="body1" color="text.secondary">
                    No events found for {currentMonth.format('MMMM YYYY')}
                  </Typography>
                </Box>
              )}
            </Box>
          </TranslucentContainer>
        </Grid>

        {/* Event Details Panel for Mobile */}
        {selectedEvent && (
          <Grid item xs={12} ref={eventDetailsPanelRef}>
            <TranslucentContainer>
              <Box sx={{ padding: 3 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  marginBottom={2}>
                  <Typography variant="h6" color="#EE7B57" fontWeight="bold">
                    Event Details
                  </Typography>
                  <IconButton
                    onClick={handleCloseEventPanel}
                    size="small"
                    sx={{
                      color: '#EE7B57',
                      '&:hover': { backgroundColor: 'rgba(238, 123, 87, 0.1)' },
                    }}>
                    <Typography variant="body2" fontWeight="bold">
                      ✕
                    </Typography>
                  </IconButton>
                </Box>

                <Box
                  component="img"
                  src={selectedEventPhoto || DefaultPhoto}
                  alt={selectedEvent.name}
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                    borderRadius: 2,
                    marginBottom: 2,
                  }}
                />

                <Typography variant="h5" color="#EE7B57" fontWeight="bold" gutterBottom>
                  {selectedEvent.name}
                </Typography>

                <Box marginBottom={2}>
                  <Typography variant="body1" color="text.primary" fontWeight="bold">
                    📅 {moment(selectedEvent.parameters_event?.event_date).format('MMMM Do, YYYY')}
                  </Typography>
                  <Typography variant="body1" color="text.primary">
                    🕒 {formatTime(selectedEvent?.parameters_event?.hour_start_event, 'h:mm A')} -{' '}
                    {formatTime(selectedEvent?.parameters_event?.hour_end_event, 'h:mm A')}
                  </Typography>
                </Box>

                <Box marginBottom={2}>
                  <Typography variant="body1" color="text.primary" fontWeight="bold">
                    📍 Venue
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {selectedEvent.venue?.name || 'Not specified'}
                  </Typography>
                  {(selectedEvent.city?.name || selectedEvent.state?.name) && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedEvent.city?.name}
                      {selectedEvent.city?.name && selectedEvent.state?.name && ', '}
                      {selectedEvent.state?.name}
                    </Typography>
                  )}
                  {selectedEvent.street_address && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedEvent.street_address}
                    </Typography>
                  )}
                </Box>

                {selectedEvent.description && (
                  <Box marginBottom={2}>
                    <Typography variant="body1" color="text.primary" fontWeight="bold" gutterBottom>
                      About
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {selectedEvent.description}
                    </Typography>
                  </Box>
                )}

                {selectedEvent.event_price && (
                  <Box marginBottom={2}>
                    <Typography variant="body1" color="text.primary" fontWeight="bold">
                      💰 Price
                    </Typography>
                    <Typography variant="h6" color="#EE7B57">
                      {Number(selectedEvent.event_price).toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD',
                      })}
                    </Typography>
                  </Box>
                )}

                <Box display="flex" flexDirection="column" gap={2} marginTop={3}>
                  <LinkButton
                    to={eventDetails(selectedEvent.id)}
                    variant="contained"
                    fullWidth
                    onClick={() => window.scrollTo(0, 0)}
                    sx={{
                      backgroundColor: '#EE7B57',
                      color: 'white',
                      textTransform: 'none',
                      padding: '10px',
                      '&:hover': { backgroundColor: '#d66a47' },
                    }}>
                    View Full Details
                    <ChevronRight sx={{ fill: 'white' }} />
                  </LinkButton>

                  {selectedEvent.status && (
                    <LinkButton
                      to={eventRegisterLink(selectedEvent.id)}
                      variant="outlined"
                      fullWidth
                      sx={{
                        borderColor: '#EE7B57',
                        color: '#EE7B57',
                        textTransform: 'none',
                        padding: '10px',
                        '&:hover': {
                          borderColor: '#d66a47',
                          backgroundColor: 'rgba(238, 123, 87, 0.1)',
                        },
                      }}>
                      Apply to This Event
                    </LinkButton>
                  )}
                </Box>
              </Box>
            </TranslucentContainer>
          </Grid>
        )}

        {/* Day Items Modal for Mobile */}
        <Dialog
          open={dayModalOpen}
          onClose={handleCloseDayModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
            },
          }}>
          <DialogTitle sx={{ backgroundColor: '#EE7B57', color: 'white' }}>
            <Typography variant="h6" fontWeight="bold">
              All Items for this Day
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ padding: 3, marginTop: 2 }}>
            {selectedDayItems.map((item) => {
              const color = item.itemType === 'event' ? EVENT_COLOR : RETAIL_COLOR;
              const icon = item.itemType === 'event' ? <EventIcon /> : <Storefront />;
              const name = item.itemType === 'event' ? item.name : item.profile?.venue?.name;

              return (
                <Card
                  key={item.id}
                  sx={{
                    marginBottom: 2,
                    borderLeft: `4px solid ${color}`,
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: 3,
                      backgroundColor: 'rgba(238, 123, 87, 0.05)',
                    },
                  }}
                  onClick={() => {
                    handleItemClick(
                      item,
                      item.itemType,
                      item.itemType === 'retail' ? modalDate : null,
                    );
                    handleCloseDayModal();
                  }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} marginBottom={1}>
                      {icon}
                      <Typography variant="h6" color={color}>
                        {name}
                      </Typography>
                    </Box>
                    {item.itemType === 'event' ? (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          🕒 {formatTime(item?.parameters_event?.hour_start_event, 'h:mm A')} -{' '}
                          {formatTime(item?.parameters_event?.hour_end_event, 'h:mm A')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          📍 {item.venue?.name || 'Venue TBD'}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          🕒 {formatRetailTime(item.schedule?.start_time)} -{' '}
                          {formatRetailTime(item.schedule?.end_time)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          💰 ${item.schedule?.price_per_hour}/hour
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </DialogContent>
          <DialogActions sx={{ padding: 2 }}>
            <Button
              onClick={handleCloseDayModal}
              sx={{
                color: '#EE7B57',
                '&:hover': {
                  backgroundColor: 'rgba(238, 123, 87, 0.1)',
                },
              }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    );
  }

  // Desktop Calendar View
  return (
    <Grid container marginTop={5} justifyContent="center" spacing={3}>
      <Grid
        item
        xs={12}
        lg={selectedEvent ? 8 : 12}
        sx={{
          transition: 'all 0.8s ease-in-out',
        }}>
        <TranslucentContainer>
          <Box sx={{ padding: 4 }}>
            {/* Calendar Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={3}>
              <IconButton onClick={previousMonth} sx={{ color: '#EE7B57' }}>
                <ChevronLeft />
              </IconButton>
              <Typography variant="h4" fontWeight="bold" color="#EE7B57">
                {currentMonth.format('MMMM YYYY')}
              </Typography>
              <IconButton onClick={nextMonth} sx={{ color: '#EE7B57' }}>
                <ChevronRight />
              </IconButton>
            </Box>

            {/* Weekday Headers */}
            <Grid container spacing={1} marginBottom={2}>
              {weekDays.map((day) => (
                <Grid item xs={12 / 7} key={day}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      fontWeight: 'bold',
                      color: '#EE7B57',
                      padding: 1,
                    }}>
                    {day}
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Calendar Grid */}
            <Grid container spacing={1}>
              {calendarDays.map((day) => {
                const dayItems = getItemsForDate(day);
                const isCurrentMonth = day.month() === currentMonth.month();
                const isToday = day.isSame(moment(), 'day');

                // Check if this day contains the selected item
                const hasSelectedItem =
                  selectedEvent &&
                  ((selectedItemType === 'event' &&
                    dayItems.events.some((e) => e.id === selectedEvent.id)) ||
                    (selectedItemType === 'retail' &&
                      selectedDate === day.format('YYYY-MM-DD') &&
                      dayItems.retail.some((r) => r.profile.id === selectedEvent.id)));

                // Determine background color based on day status
                let backgroundColor = 'rgba(240, 240, 240, 0.5)'; // Other month
                if (isCurrentMonth) {
                  if (hasSelectedItem) {
                    backgroundColor = 'rgba(74, 144, 226, 0.08)'; // Light blue for selected
                  } else if (isToday) {
                    backgroundColor = 'rgba(238, 123, 87, 0.1)'; // Light orange for today
                  } else {
                    backgroundColor = 'rgba(255, 255, 255, 0.9)'; // White for other days
                  }
                }

                // Determine border style
                let borderStyle = '1px solid #e0e0e0';
                if (hasSelectedItem) {
                  borderStyle = '2px solid #4A90E2'; // Blue for selected
                } else if (isToday) {
                  borderStyle = '2px solid #EE7B57'; // Orange for today
                }

                const totalItems = dayItems.all.length;

                return (
                  <Grid item xs={12 / 7} key={day.format('YYYY-MM-DD')}>
                    <Card
                      sx={{
                        height: 140,
                        maxHeight: 140,
                        backgroundColor,
                        border: borderStyle,
                        transition: 'all 0.3s',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                          boxShadow: 3,
                          transform: 'translateY(-2px)',
                        },
                      }}>
                      <CardContent
                        sx={{
                          padding: 1,
                          flex: 1,
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                        }}>
                        {/* Date Number */}
                        <Typography
                          variant="body2"
                          fontWeight={isToday ? 'bold' : 'normal'}
                          color={isCurrentMonth ? 'text.primary' : 'text.disabled'}
                          marginBottom={0.5}
                          sx={{ flexShrink: 0 }}>
                          {day.format('D')}
                        </Typography>

                        {/* Items (Events & Retail) for this day */}
                        {totalItems > 0 && (
                          <Box
                            sx={{
                              flex: 1,
                              overflow: 'hidden',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.4,
                            }}>
                            {dayItems.all.slice(0, 2).map((item) => {
                              const color = item.itemType === 'event' ? EVENT_COLOR : RETAIL_COLOR;
                              const icon = item.itemType === 'event' ? '📅' : '🏪';
                              const displayName =
                                item.itemType === 'event' ? item.name : item.profile?.venue?.name;

                              return (
                                <Box
                                  key={item.id}
                                  sx={{
                                    backgroundColor: color,
                                    color: 'white',
                                    borderRadius: 1,
                                    padding: '3px 5px',
                                    fontSize: '0.7rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    lineHeight: 1.3,
                                    '&:hover': {
                                      opacity: 0.85,
                                      transform: 'scale(1.02)',
                                    },
                                  }}
                                  onClick={() =>
                                    handleItemClick(
                                      item,
                                      item.itemType,
                                      item.itemType === 'retail' ? day.format('YYYY-MM-DD') : null,
                                    )
                                  }
                                  title={`${icon} ${displayName}`}>
                                  {icon} {displayName}
                                </Box>
                              );
                            })}
                            {totalItems > 2 && (
                              <Typography
                                variant="caption"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDayModal(dayItems.all, day.format('YYYY-MM-DD'));
                                }}
                                sx={{
                                  color: '#EE7B57',
                                  fontWeight: 'bold',
                                  fontSize: '0.68rem',
                                  cursor: 'pointer',
                                  lineHeight: 1.2,
                                  marginTop: '3px',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                    opacity: 0.8,
                                  },
                                }}>
                                +{totalItems - 2} more
                              </Typography>
                            )}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </TranslucentContainer>
      </Grid>

      {/* Detail Panel (Event or Retail) */}
      {selectedEvent && (
        <Grid
          item
          xs={12}
          lg={4}
          ref={eventDetailsPanelRef}
          sx={{
            animation: 'slideIn 0.4s ease-in-out',
            '@keyframes slideIn': {
              from: {
                opacity: 0,
                transform: 'translateX(20px)',
              },
              to: {
                opacity: 1,
                transform: 'translateX(0)',
              },
            },
          }}>
          <TranslucentContainer>
            <Box
              sx={{
                padding: 3,
                position: 'sticky',
                top: 20,
                maxHeight: 'calc(100vh - 100px)',
                overflowY: 'auto',
              }}>
              {/* Header with Type Badge and Close Button */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                marginBottom={2}>
                <Chip
                  icon={selectedItemType === 'event' ? <EventIcon /> : <Storefront />}
                  label={selectedItemType === 'event' ? 'Event' : 'Retail Opportunity'}
                  sx={{
                    bgcolor: selectedItemType === 'event' ? EVENT_COLOR : RETAIL_COLOR,
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                />
                <IconButton
                  onClick={handleCloseEventPanel}
                  size="small"
                  sx={{
                    color: '#EE7B57',
                    '&:hover': {
                      backgroundColor: 'rgba(238, 123, 87, 0.1)',
                    },
                  }}>
                  <Typography variant="body2" fontWeight="bold">
                    ✕
                  </Typography>
                </IconButton>
              </Box>

              {selectedItemType === 'event' ? (
                // Event Details
                <>
                  <Box
                    component="img"
                    src={selectedEventPhoto || DefaultPhoto}
                    alt={selectedEvent.name}
                    sx={{
                      width: '100%',
                      height: 200,
                      objectFit: 'cover',
                      borderRadius: 2,
                      marginBottom: 2,
                    }}
                  />

                  <Typography variant="h5" color="#EE7B57" fontWeight="bold" gutterBottom>
                    {selectedEvent.name}
                  </Typography>

                  <Box marginBottom={2}>
                    <Typography variant="body1" color="text.primary" fontWeight="bold">
                      📅{' '}
                      {moment(selectedEvent.parameters_event?.event_date).format('MMMM Do, YYYY')}
                    </Typography>
                    <Typography variant="body1" color="text.primary">
                      🕒 {formatTime(selectedEvent?.parameters_event?.hour_start_event, 'h:mm A')}
                      {' - '}
                      {formatTime(selectedEvent?.parameters_event?.hour_end_event, 'h:mm A')}
                    </Typography>
                  </Box>

                  <Box marginBottom={2}>
                    <Typography variant="body1" color="text.primary" fontWeight="bold">
                      📍 Venue
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {selectedEvent.venue?.name || 'Not specified'}
                    </Typography>
                    {(selectedEvent.city?.name || selectedEvent.state?.name) && (
                      <Typography variant="body2" color="text.secondary">
                        {selectedEvent.city?.name}
                        {selectedEvent.city?.name && selectedEvent.state?.name && ', '}
                        {selectedEvent.state?.name}
                      </Typography>
                    )}
                  </Box>

                  {selectedEvent.description && (
                    <Box marginBottom={2}>
                      <Typography
                        variant="body1"
                        color="text.primary"
                        fontWeight="bold"
                        gutterBottom>
                        About
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {selectedEvent.description}
                      </Typography>
                    </Box>
                  )}

                  {selectedEvent.event_price && (
                    <Box marginBottom={2}>
                      <Typography variant="body1" color="text.primary" fontWeight="bold">
                        💰 Price
                      </Typography>
                      <Typography variant="h6" color="#EE7B57">
                        {Number(selectedEvent.event_price).toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'USD',
                        })}
                      </Typography>
                    </Box>
                  )}

                  <Box display="flex" flexDirection="column" gap={2} marginTop={3}>
                    <LinkButton
                      to={eventDetails(selectedEvent.id)}
                      variant="contained"
                      fullWidth
                      sx={{
                        backgroundColor: '#EE7B57',
                        color: 'white',
                        textTransform: 'none',
                        padding: '10px',
                        '&:hover': { backgroundColor: '#d66a47' },
                      }}>
                      View Full Details
                      <ChevronRight sx={{ fill: 'white' }} />
                    </LinkButton>

                    {selectedEvent.status && (
                      <LinkButton
                        to={eventRegisterLink(selectedEvent.id)}
                        variant="outlined"
                        fullWidth
                        sx={{
                          borderColor: '#EE7B57',
                          color: '#EE7B57',
                          textTransform: 'none',
                          padding: '10px',
                          '&:hover': {
                            borderColor: '#d66a47',
                            backgroundColor: 'rgba(238, 123, 87, 0.1)',
                          },
                        }}>
                        Apply to This Event
                      </LinkButton>
                    )}
                  </Box>
                </>
              ) : (
                // Retail Opportunity Details
                <>
                  <Typography variant="h5" color="#3c99ba" fontWeight="bold" gutterBottom>
                    {selectedEvent?.venue?.name}
                  </Typography>

                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    Retail Space Opportunity
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Box marginBottom={2}>
                    <Typography variant="body1" color="text.primary" fontWeight="bold">
                      📍 Location
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedEvent?.venue?.business_address || 'Address not specified'}
                    </Typography>
                    {(selectedEvent?.venue?.city?.name || selectedEvent?.venue?.state?.name) && (
                      <Typography variant="body2" color="text.secondary">
                        {selectedEvent?.venue?.city?.name}
                        {selectedEvent?.venue?.city?.name &&
                          selectedEvent?.venue?.state?.name &&
                          ', '}
                        {selectedEvent?.venue?.state?.name}
                      </Typography>
                    )}
                  </Box>

                  <Box marginBottom={2}>
                    <Typography variant="body1" color="text.primary" fontWeight="bold">
                      📅 Availability Period
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedEvent?.start_date
                        ? moment(selectedEvent.start_date).format('MMM DD, YYYY')
                        : 'Open'}{' '}
                      -{' '}
                      {selectedEvent?.end_date
                        ? moment(selectedEvent.end_date).format('MMM DD, YYYY')
                        : 'Ongoing'}
                    </Typography>
                  </Box>

                  <Box marginBottom={2}>
                    <Typography variant="body1" color="text.primary" fontWeight="bold" gutterBottom>
                      🕒 Available Time Slots
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      Click a time slot to book directly
                    </Typography>
                    {(() => {
                      // If a specific date was selected, only show that day's schedule
                      const schedulesToShow = selectedDate
                        ? (selectedEvent?.schedules || []).filter(
                            (schedule) =>
                              schedule.day_of_week?.toLowerCase() ===
                              moment(selectedDate).format('dddd').toLowerCase(),
                          )
                        : (selectedEvent?.schedules || []).slice(0, 3);

                      return schedulesToShow.map((schedule) => (
                        <Card
                          key={schedule.id}
                          onClick={() => {
                            // Navigate to retail booking page with prepopulated date
                            // selectedDate is a string, so convert to moment or use fallback
                            const bookingDate = selectedDate
                              ? moment(selectedDate)
                              : moment().day(schedule.day_of_week);
                            navigate(retailBooking(selectedEvent.id), {
                              state: { preselectedDate: bookingDate.format('YYYY-MM-DD') },
                            });
                          }}
                          sx={{
                            mb: 1,
                            p: 1.5,
                            bgcolor: '#F5F5F5',
                            border: '1px solid #E0E0E0',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: '#E8F5F9',
                              borderColor: '#3c99ba',
                              boxShadow: 2,
                              transform: 'translateY(-2px)',
                            },
                          }}>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ textTransform: 'capitalize' }}>
                            {schedule.day_of_week}
                            {selectedDate && (
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{ ml: 1, color: '#3c99ba', fontWeight: 'normal' }}>
                                ({moment(selectedDate).format('MMM D, YYYY')})
                              </Typography>
                            )}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                            <Schedule sx={{ fontSize: 16, color: '#666' }} />
                            <Typography variant="caption">
                              {formatRetailTime(schedule.start_time)}
                              {' - '}
                              {formatRetailTime(schedule.end_time)}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                            <AttachMoney sx={{ fontSize: 16, color: '#3c99ba' }} />
                            <Typography variant="caption">
                              ${schedule.price_per_hour}/hr • {schedule.slots_available} slots
                            </Typography>
                          </Box>
                        </Card>
                      ));
                    })()}
                    {!selectedDate && (selectedEvent?.schedules?.length || 0) > 3 && (
                      <Typography variant="caption" color="text.secondary">
                        +{selectedEvent.schedules.length - 3} more days available
                      </Typography>
                    )}
                  </Box>

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate(retailDetails(selectedEvent.id))}
                    sx={{
                      bgcolor: '#3c99ba',
                      color: 'white',
                      textTransform: 'none',
                      padding: '12px',
                      mt: 2,
                      '&:hover': { bgcolor: '#3db3aa' },
                    }}>
                    View Full Details
                  </Button>
                </>
              )}
            </Box>
          </TranslucentContainer>
        </Grid>
      )}

      {/* Day Items Modal for Desktop */}
      <Dialog
        open={dayModalOpen}
        onClose={handleCloseDayModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}>
        <DialogTitle sx={{ backgroundColor: '#EE7B57', color: 'white' }}>
          <Typography variant="h6" fontWeight="bold">
            All Items for this Day
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: 3, marginTop: 2 }}>
          {selectedDayItems.map((item) => {
            const color = item.itemType === 'event' ? EVENT_COLOR : RETAIL_COLOR;
            const icon = item.itemType === 'event' ? <EventIcon /> : <Storefront />;
            const name = item.itemType === 'event' ? item.name : item.profile?.venue?.name;

            return (
              <Card
                key={item.id}
                sx={{
                  marginBottom: 2,
                  borderLeft: `4px solid ${color}`,
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 3,
                    backgroundColor: 'rgba(238, 123, 87, 0.05)',
                  },
                }}
                onClick={() => {
                  handleItemClick(
                    item,
                    item.itemType,
                    item.itemType === 'retail' ? modalDate : null,
                  );
                  handleCloseDayModal();
                }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} marginBottom={1}>
                    {icon}
                    <Typography variant="h6" color={color}>
                      {name}
                    </Typography>
                  </Box>
                  {item.itemType === 'event' ? (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        🕒 {formatTime(item?.parameters_event?.hour_start_event, 'h:mm A')} -{' '}
                        {formatTime(item?.parameters_event?.hour_end_event, 'h:mm A')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        📍 {item.venue?.name || 'Venue TBD'}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        🕒 {formatRetailTime(item.schedule?.start_time)} -{' '}
                        {formatRetailTime(item.schedule?.end_time)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        💰 ${item.schedule?.price_per_hour}/hour
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button
            onClick={handleCloseDayModal}
            sx={{
              color: '#EE7B57',
              '&:hover': {
                backgroundColor: 'rgba(238, 123, 87, 0.1)',
              },
            }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

CalendarView.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      status: PropTypes.bool.isRequired,
      street_address: PropTypes.string,
      parameters_event: PropTypes.shape({
        event_date: PropTypes.string,
        hour_start_event: PropTypes.string,
        hour_end_event: PropTypes.string,
      }),
      venue: PropTypes.shape({
        name: PropTypes.string,
      }),
      city: PropTypes.shape({
        name: PropTypes.string,
      }),
      state: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
  ).isRequired,
  retailProfiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      status: PropTypes.string,
      start_date: PropTypes.string,
      end_date: PropTypes.string,
      venue: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        business_address: PropTypes.string,
        city: PropTypes.shape({
          name: PropTypes.string,
        }),
        state: PropTypes.shape({
          name: PropTypes.string,
          code: PropTypes.string,
        }),
      }),
      schedules: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          day_of_week: PropTypes.string,
          start_time: PropTypes.string,
          end_time: PropTypes.string,
          price_per_hour: PropTypes.number,
          slots_available: PropTypes.number,
        }),
      ),
    }),
  ),
  dateFilterStart: PropTypes.string,
  dateFilterEnd: PropTypes.string,
  priceFilterMin: PropTypes.string,
  priceFilterMax: PropTypes.string,
};

CalendarView.defaultProps = {
  retailProfiles: undefined,
  dateFilterStart: undefined,
  dateFilterEnd: undefined,
  priceFilterMin: undefined,
  priceFilterMax: undefined,
};

export default CalendarView;
